import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Moon } from 'lucide-react';
import { arenaAudio } from '../../../services/ArenaAudioService';

export interface HeartsShootTheMoonAnimationProps {
  isOpen: boolean;
  isUserShooter?: boolean;
  shooterName?: string;
  shooterAvatarUrl?: string;
  isBot?: boolean;
  shootTheMoonRule?: string;
  shootTheMoonChoice?: string | null;
  points?: number;
}

const HeartsShootTheMoonAnimationComponent: React.FC<HeartsShootTheMoonAnimationProps> = ({
  isOpen,
  isUserShooter = false,
  shooterName = 'Player',
  shooterAvatarUrl,
  isBot = false,
  shootTheMoonRule = 'CHOICE',
  shootTheMoonChoice,
  points = 26,
}) => {
  const soundPlayedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !soundPlayedRef.current) {
      soundPlayedRef.current = true;
      const timer = window.setTimeout(() => {
        try {
          arenaAudio.playShootTheMoon();
        } catch {
          // ignore audio errors
        }
      }, 250);
      return () => window.clearTimeout(timer);
    }
    if (!isOpen) {
      soundPlayedRef.current = false;
    }
  }, [isOpen]);

  const outcomeSubtitle = React.useMemo(() => {
    if (shootTheMoonChoice === 'MINUS_SELF' || shootTheMoonRule === 'SUBTRACT_FROM_SELF') {
      return `-${points} Points to ${isUserShooter ? 'You' : shooterName}!`;
    }
    return `+${points} Penalty to All Opponents!`;
  }, [shootTheMoonChoice, shootTheMoonRule, points, isUserShooter, shooterName]);

  const displayName = isUserShooter ? 'You' : shooterName;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="hearts-moon-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }}
          data-testid="hearts-shoot-the-moon-animation"
          className="fixed inset-0 pointer-events-none z-[65] overflow-hidden select-none bg-transparent flex flex-col items-center justify-start pt-14 sm:pt-20"
          aria-label="Shoot the Moon Celebration"
        >
          {/* Fancy Static Graphic Banner: X Shot the Moon */}
          <motion.div
            initial={{ scale: 0.85, y: -24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300, delay: 0.05 }}
            className="relative flex flex-col items-center max-w-[92vw] sm:max-w-md mx-auto"
          >
            {/* Ambient luxury backdrop glow */}
            <div className="absolute -inset-2 sm:-inset-3 rounded-3xl bg-gradient-to-r from-purple-600/35 via-rose-500/40 to-amber-500/35 blur-xl pointer-events-none" />

            {/* Premium Gold & Velvet Graphic Card */}
            <div className="relative z-10 w-full px-5 sm:px-7 py-3.5 sm:py-4 rounded-3xl bg-gradient-to-b from-slate-950/95 via-purple-950/95 to-slate-950/95 border-2 border-yellow-400/80 shadow-[0_16px_40px_rgba(0,0,0,0.85),0_0_30px_rgba(244,114,182,0.35)] flex flex-col items-center text-center">
              {/* Header Title with Moon Crest */}
              <div
                data-testid="shoot-moon-banner"
                className="flex items-center justify-center gap-2 sm:gap-2.5 mb-2.5 px-4 py-1 rounded-full bg-gradient-to-r from-amber-500/20 via-yellow-400/25 to-amber-500/20 border border-yellow-400/40"
              >
                <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300 fill-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]" />
                <span className="text-[11px] sm:text-xs font-black uppercase tracking-widest text-amber-200 font-serif drop-shadow-sm">
                  SHOT THE MOON!
                </span>
                <span className="text-sm select-none">🌕</span>
              </div>

              {/* Shooter Avatar + Graphic Announcement */}
              <div className="flex items-center gap-3 sm:gap-4 my-1">
                <div className="relative w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-slate-900 border-2 border-yellow-400 overflow-hidden flex items-center justify-center shadow-[0_0_15px_rgba(250,204,21,0.5)] shrink-0">
                  {shooterAvatarUrl ? (
                    <img src={shooterAvatarUrl} alt={shooterName} className="w-full h-full object-cover" />
                  ) : isBot ? (
                    <Bot className="w-7 h-7 sm:w-8 sm:h-8 text-pink-300" />
                  ) : (
                    <span className="text-base sm:text-lg font-black text-amber-200">{shooterName.slice(0, 2).toUpperCase()}</span>
                  )}
                  {isUserShooter && (
                    <span className="absolute -top-1 -right-1 text-xs filter drop-shadow">👑</span>
                  )}
                </div>

                <div className="text-left">
                  <div
                    data-testid="shooter-name"
                    className="text-base sm:text-xl font-black text-white leading-tight tracking-tight flex items-center gap-1.5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
                  >
                    <span>{displayName} Shot The Moon!</span>
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-pink-300/95 leading-snug mt-0.5">
                    {outcomeSubtitle}
                  </div>
                </div>
              </div>

              {/* Clean Sweep Distinction Ribbon */}
              <div className="text-[10px] sm:text-[11px] font-mono font-black uppercase tracking-wider text-yellow-300/90 border-t border-yellow-400/20 pt-2 mt-2.5 w-full flex items-center justify-center gap-2">
                <span>★</span>
                <span>CLEAN SWEEP • ALL 26 POINTS</span>
                <span>★</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const HeartsShootTheMoonAnimation = React.memo(HeartsShootTheMoonAnimationComponent);
